import { localizeServiceName } from '@/utils/serviceNames';
import React, { memo } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { Service } from '@/data/types';
import { colors, radius, spacing, type, useAppTheme } from '@/theme';
import { PriceTag } from '../ui/PriceTag';
import { ScaledSheet } from '@/theme/responsive';
import { formatDuration } from '@/utils/geo';

interface Props {
  service: Service;
  selected?: boolean;
  onPress?: () => void;
}

function ServiceCardComponent({ service, selected, onPress }: Props) {
  const { colors } = useAppTheme();
  const hasDiscount = !!service.discountPrice && service.discountPrice < service.price;
  return (
    <Pressable
      accessibilityRole={onPress ? 'checkbox' : undefined}
      accessibilityState={onPress ? { checked: !!selected } : undefined}
      onPress={onPress}
      style={[
        styles.card,
        // Picked: a soft accent tint from the current theme (the fixed light palette used here before turned the
        // card white in dark mode and hid its light text).
        { backgroundColor: selected ? colors.accentSoft : colors.surface, borderColor: selected ? colors.accent : colors.border },
      ]}>
      {!!service.imageUrl && (
        <Image
          source={{ uri: service.imageUrl }}
          style={[styles.thumbnail, { backgroundColor: colors.surfaceMuted }]}
          contentFit="cover"
          transition={150}
        />
      )}
      <View style={styles.info}>
        <View style={styles.titleRow}>
          <Text style={[type.titleLg, { color: colors.textPrimary, flexShrink: 1 }]}>{localizeServiceName(service.name)}</Text>
          {hasDiscount && <View style={[styles.discountPill, { backgroundColor: colors.errorSoft }]}><Text style={[styles.discountText, { color: colors.error }]}>SALE</Text></View>}
        </View>
        <Text style={[type.bodySm, styles.description, { color: colors.textSecondary }]} numberOfLines={2}>{service.description}</Text>
        <View style={styles.metaRow}>
          <Ionicons name="time-outline" size={13} color={colors.textTertiary} />
          <Text style={[type.caption, styles.duration, { color: colors.textTertiary }]}>{formatDuration(service.durationMin)}</Text>
          <View style={[styles.dot, { backgroundColor: colors.textTertiary }]} />
          <PriceTag price={service.discountPrice ?? service.price} originalPrice={hasDiscount ? service.price : undefined} size="sm" />
        </View>
      </View>
      {onPress && (
        <View style={[
          styles.checkbox,
          { borderColor: selected ? colors.accent : colors.borderStrong },
          selected && { backgroundColor: colors.accentFill },
        ]}>
          {selected && <Ionicons name="checkmark" size={15} color={colors.textOnAccent} />}
        </View>
      )}
    </Pressable>
  );
}

export const ServiceCard = memo(ServiceCardComponent);

const styles = ScaledSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1.5,
    borderColor: colors.border,
    padding: spacing.lg,
  },
  thumbnail: { width: 56, height: 56, borderRadius: radius.md, backgroundColor: colors.surfaceMuted },
  info: { flex: 1, gap: 4 },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  discountPill: { backgroundColor: colors.errorSoft, paddingHorizontal: 6, paddingVertical: 2, borderRadius: radius.xs },
  discountText: { fontSize: 9, fontWeight: '800', color: colors.error, letterSpacing: 0.5 },
  description: { color: colors.textSecondary },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 2 },
  duration: { color: colors.textTertiary },
  dot: { width: 3, height: 3, borderRadius: 1.5, backgroundColor: colors.textTertiary },
  checkbox: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 1.5,
    borderColor: colors.borderStrong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: { backgroundColor: colors.accentFill, borderColor: colors.accent },
});
